import { useState } from 'react';
import { Button, TextInput } from '../../components/ui';
import { MEDIA_TYPES, MEDIA_TYPE_LABELS, isMediaType, type MediaType } from '../../lib/dailies';
import { MAX_TITLE } from './useMedia';

/** Matches TextInput's chrome; ui.tsx has no shared <select> primitive. */
const SELECT_CLASS =
  'h-7 shrink-0 rounded-lg border border-line bg-surface px-1 text-[12px] text-ink focus:border-accent focus:outline-none';

/**
 * Collapsible add form, rendered in the Card footer only while open (the `+`
 * in the header toggles it) so it never permanently costs a list row in a
 * ~350x330 card.
 *
 * The release date is optional. A blank or malformed date is a legitimate
 * 'invalid' row that renders with an em dash rather than being rejected —
 * the user may not know the date yet.
 */
export function MediaAddForm({
  onAdd,
  onClose,
}: {
  onAdd: (title: string, releaseDate: string, type: MediaType) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState('');
  const [releaseDate, setReleaseDate] = useState('');
  const [type, setType] = useState<MediaType>('game');

  const submit = () => {
    if (title.trim() === '') return;
    onAdd(title, releaseDate, type);
    setTitle('');
    setReleaseDate('');
  };

  return (
    <div className="flex flex-col gap-1.5">
      <TextInput
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit();
          if (e.key === 'Escape') onClose();
        }}
        maxLength={MAX_TITLE}
        placeholder="Title…"
        aria-label="Title"
        autoFocus
        className="h-7 w-full py-0"
      />
      <div className="flex items-center gap-1.5">
        <TextInput
          type="date"
          value={releaseDate}
          onChange={(e) => setReleaseDate(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
          }}
          aria-label="Release date"
          className="h-7 min-w-0 flex-1 py-0 text-[12px] [color-scheme:light] dark:[color-scheme:dark]"
        />
        <select
          value={type}
          onChange={(e) => setType(isMediaType(e.target.value) ? e.target.value : 'other')}
          aria-label="Type"
          className={SELECT_CLASS}
        >
          {MEDIA_TYPES.map((t) => (
            <option key={t} value={t}>
              {MEDIA_TYPE_LABELS[t]}
            </option>
          ))}
        </select>
        <Button variant="accent" onClick={submit} disabled={title.trim() === ''} className="py-1">
          Add
        </Button>
      </div>
    </div>
  );
}
